import React, { useCallback, useState } from 'react';
import { Row, Col, Button, InputGroup, Form, OverlayTrigger, Popover, Card } from 'react-bootstrap-5';
import { connect, useSelector } from 'react-redux';

import { type Activity, type CallList, type CallListItem, type InsightsProperty } from 'bundles/connect/types';
import RefreshOwnersModal from 'bundles/connect/components/off_market_properties/owners/refresh_owners_modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCaretDown, faSearch } from '@fortawesome/pro-solid-svg-icons';
import {
  fetchCallListItems,
  callListUrl,
  CALL_LISTS_INDEX,
  CALL_LISTS_NEXT,
  CALL_LISTS_SEARCH,
  CALL_LISTS_SHOW
} from 'bundles/connect/helpers/prospect_api';
import { debounce } from 'lodash';
import { formatDateString } from '../../helpers/date_helpers';
import ScoreBadges from '../search/components/results/score_badges';
import { setIndex } from 'bundles/connect/components/store/actions/call_console';
import { selectHasUnlimitedUnlocks } from '../store/selectors/ui';


export type CallConsoleHeaderProps = {
  callConsoleData: CallConsoleResponse;
  callListLength: number;
  remainingCredits: number;
  setIndex: (url: string) => Promise<void>;
};

type CallConsoleResponse = {
  callList: CallList;
  insightsProperty: InsightsProperty;
  index: number;
  lastFetched: string | null;
  connectPropertyId: number;
  activities: Activity[];
}

function CallConsoleHeader({
  callConsoleData,
  callListLength,
  remainingCredits,
  setIndex,
}: CallConsoleHeaderProps) {
  const [callListItems, setCallListItems] = useState<CallListItem[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [showPopover, setShowPopover] = useState(false);

  const unlimitedUnlocks = useSelector(selectHasUnlimitedUnlocks);
  const insightsProperty = callConsoleData.insightsProperty;
  const callList = callConsoleData.callList;
  const index = callConsoleData.index;
  const lastFetched = callConsoleData.lastFetched;
  const likelyToSell = insightsProperty?.scores.likelyToSell && insightsProperty.scores.likelyToSell.rank > 0;
  const likelyToRefi = insightsProperty?.scores.likelyToRefi && insightsProperty.scores.likelyToRefi.rank > 0;

  const debouncedSearch = useCallback(
    debounce((query) => {
      search(query);
    }, 300),
    []
  );

  const search = (query: string) => {
    const params = {
      'q[call_listable_of_Connect::Property_type_address_cont]': query
    };
    fetchCallListItems(params, callList.id).then((data) => {
      setCallListItems(data.data);
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(event.target.value);
  };

  const handleDropdownClick = () => {
    if (callListItems.length === 0) {
      search('');
    }
  };

  const handlePrevNext = async (url: string) => {
    setDisabled(true);
    await setIndex(url);
    setDisabled(false);
  };

  return (
    <>
      <Row className="gy-3">
        <Col className="order-2 order-lg-1" lg>
          <Row className="flex-column gap-2">
            <Col>
              <nav>
                <ol className="breadcrumb my-0 align-items-center">
                  <li className="breadcrumb-item my-0">
                    <a className="text-muted" href={callListUrl(CALL_LISTS_SEARCH)}>
                      <i className="far fa-home"></i>
                    </a>
                  </li>
                  <li className="breadcrumb-item my-0">
                    <a className="text-muted" href={callListUrl(CALL_LISTS_INDEX)}>
                      Lists
                    </a>
                  </li>
                  <li className="breadcrumb-item my-0">
                    <a className="text-muted" href={callListUrl(CALL_LISTS_SHOW, callList.id)}>
                      {callList.name}
                    </a>
                  </li>
                </ol>
              </nav>
            </Col>
            {insightsProperty && (
              <>
                {(likelyToSell || likelyToRefi) && (
                  <Col>
                    <Row className="g-1">
                      <ScoreBadges scores={insightsProperty.scores} />
                    </Row>
                  </Col>
                )}
                <Col>
                  <h5 className="m-0">{insightsProperty.address}</h5>
                </Col>
                <Col>
                  <div className="text-muted m-0">
                    {insightsProperty.city}, {insightsProperty.state} {insightsProperty.zip}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Col>
        <Col className="order-lg-2" lg="auto">
          <Row className="align-items-center justify-content-center gx-1">
            {(callConsoleData.lastFetched && !unlimitedUnlocks) && (
              <Col xs="auto">
                <RefreshOwnersModal
                  activityConsole={true}
                  insightsPropertyId={insightsProperty?.propertyId}
                  remainingCredits={remainingCredits}
                />
              </Col>
            )}
            <Col xs="auto">
              <Button
                data-turbo="false"
                disabled={disabled}
                variant="text-primary"
                onClick={() => handlePrevNext(callListUrl(CALL_LISTS_NEXT, callList.id, index - 1))}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Previous
              </Button>
            </Col>
            <Col className="text-muted" xs="auto">
              <OverlayTrigger
                overlay={
                  <Popover id="properties-popover" style={{ maxWidth: '500px' }}>
                    <Popover.Body className="p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <Card>
                        <Card.Header>
                          <Row>
                            <Col xs={6}>
                              <h6>{callList.name}</h6>
                            </Col>
                            <Col className="text-end" xs={6}>
                              <div className="text-muted">{callListLength} Properties</div>
                            </Col>
                            <Col xs={12}>
                              <InputGroup className="flex-nowrap">
                                <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                                <Form.Control
                                  placeholder="Search for address"
                                  onChange={handleChange}
                                />
                              </InputGroup>
                            </Col>
                          </Row>
                        </Card.Header>
                        <Card.Body className="overflow-auto p-0">
                          <div className="list-group list-group-flush">
                            {callListItems.map((item, index) => (
                              <a className="list-group-item"
                                href={`${callListUrl(CALL_LISTS_SHOW, callList.id)}/call_console?item_id=${item.id}`}
                                key={index}
                              >
                                {item.callListable.name}
                              </a>
                            ))}
                          </div>
                        </Card.Body>
                      </Card>
                    </Popover.Body>
                  </Popover>
                }
                placement="bottom"
                rootClose
                show={showPopover}
                trigger="click"
                onToggle={show => setShowPopover(show)}
              >
                <Button className="fw-normal fs-normal" variant="text-primary" onClick={handleDropdownClick}>
                  {index + 1} of {callListLength}
                  <FontAwesomeIcon className="ms-1" icon={faCaretDown} />
                </Button>
              </OverlayTrigger>
            </Col>
            <Col xs="auto">
              <Button
                data-turbo="false"
                disabled={disabled}
                variant="text-primary"
                onClick={() => handlePrevNext(callListUrl(CALL_LISTS_NEXT, callList.id, index + 1))}
              >
                Next
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Col>
            {index >= (callListLength - 1) && (
              <Col xs="auto">
                <Button
                  href={callListUrl(CALL_LISTS_NEXT, callList.id, index + 1)}
                  variant="secondary"
                >
                  Done
                </Button>
              </Col>
            )}
          </Row>
          {lastFetched && (
            <Row className="justify-content-end pt-2">
              <Col xs="auto">
                <span className="text-muted m-0">
                  <i className="fa-regular fa-circle-check me-1"></i>
                  Contact Data Fetched on {formatDateString(lastFetched)}
                </span>
              </Col>
            </Row>
          )}
          <div className="d-none d-lg-block d-xl-none text-end" id="js-header-portal-tablet"></div>
        </Col>
        <Col className="d-lg-none order-3" id="js-header-portal-mobile" xs={12}>
        </Col>
      </Row>
    </>
  );
}

const mapStateToProps = ({ callConsoleData }) => ({ callConsoleData });
export default connect(mapStateToProps, { setIndex })(CallConsoleHeader);
